import { Button, FileInput, Label, Textarea, ToggleSwitch } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company, ReferentMutation } from '../../../../@types/Company';
import type { LeadFormData } from '../../../../@types/contactInteraction';
import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { CoproReferentMappingData } from '../../../../@types/MailConfig';
import type { Owner } from '../../../../@types/Owner';
import { useReferentSendAvailability } from '../../../../hooks/useReferentSendAvailability';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization } from '../../../../utils/JWTHeader';
import { generateRgpdVisitDocx } from '../../../../utils/rgpdVisitDocx';
import Popup from '../../../common/Popup/Popup.module';

interface LeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  owner: Owner;
  onSubmit: (data: LeadFormData) => void;
}

const LeadForm: React.FC<LeadFormProps> = ({ isOpen, onClose, owner, onSubmit }) => {
  const [formData, setFormData] = useState<LeadFormData>({
    interactionAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
    interactionType: ContactInteractionType.PHYSICAL,
    meetingAt: null,
    meetingWith: null,
    meetingWithType: 'sales-manager',
    signedFile: null,
    signedAt: null,
    professionalActionDone: false,
    clientClosed: false,
    data: {},
    comment: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company) as Company | null;
  const { getOwnerCompany, getServedReferentEmail } = useReferentSendAvailability(company?.id);
  const [brandingReferents, setBrandingReferents] = useState<ReferentMutation[]>([]);
  const [coproMappings, setCoproMappings] = useState<CoproReferentMappingData[]>([]);

  useEffect(() => {
    if (owner.step && owner.step.includes('SALES')) {
      setFormData(prev => ({ ...prev, meetingWithType: 'sales-manager' }));
    } else if (owner.step && owner.step.includes('RENT')) {
      setFormData(prev => ({ ...prev, meetingWithType: 'rent-manager' }));
    }
  }, [owner]);

  useEffect(() => {
    const fetchMailConfig = async() => {
      if (!company?.id || !jwt) {
        return;
      }
      try {
        const res = await fetch(`/api/companies/${company.id}/mail-config/referents`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (res.ok) {
          const json = await res.json();
          setBrandingReferents(json.brandingReferents ?? []);
          setCoproMappings(json.coproMappings ?? []);
        }
      } catch (error) {
        console.error('[LeadForm] Error fetching mail config:', error);
      }
    };
    fetchMailConfig();
  }, [company?.id, jwt]);

  const getReferentEmailForOwner = (): string | null => {
    const coproId = owner.dpe?.copro?.id ?? owner.dpe?.coproId ?? owner.incomingCallCopro?.id;
    if (!coproId) {
      return null;
    }
    const mapping = coproMappings.find((m) => m.coproId === coproId);
    if (!mapping) {
      return null;
    }
    const referent = brandingReferents.find((r) => r.id === mapping.brandingReferentId);
    return referent?.email || null;
  };

  const signedFileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleChange = (name: string) => (checked: boolean) => {
    const updatedFormData = { ...formData, [name]: checked };
    
    // If professionalActionDone is toggled off, clientClosed should also be off
    if (name === 'professionalActionDone' && !checked && formData.clientClosed) {
      updatedFormData.clientClosed = false;
    }
    
    setFormData(updatedFormData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real application, you would upload the file to your server here
      // and get back a URL. For now, we'll use the file name
      setFormData((prev) => ({ 
        ...prev, 
        signedFile: file.name,
        signedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
      }));
    }
  };

  const submitLeadInteraction = async(data: LeadFormData) => {
    if (!jwt) { 
      return; 
    }
    
    setIsSubmitting(true);
    try {
      // Extract only the fields expected by the backend
      const payload = {
        interactionAt: data.interactionAt,
        meetingWithType: data.meetingWithType,
        signedFile: data.signedFile,
        signedAt: data.signedAt,
        professionalActionDone: data.professionalActionDone,
        clientClosed: data.clientClosed,
        data: data.data || {},
        comment: data.comment,
      };

      const url = `/api/owners/${owner.id}/lead-interactions`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': JWTAuthorization(jwt),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.message || 'Erreur lors de la création de l\'interaction');
      }

      // Call the onSubmit callback with the response data
      const responseData = await response.json();
      onSubmit(responseData);
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Failed to submit lead interaction:', error);
      alert(`Erreur: ${error.message || 'Une erreur est survenue lors de l\'enregistrement'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Final validation before submitting
    if (formData.clientClosed && !formData.professionalActionDone) {
      // Can't close client without professional action
      alert("L'action professionnelle doit être effectuée pour transformer en client");
      return;
    }
    
    // Submit to API
    submitLeadInteraction(formData);
  };

  const handleDownloadRGPDDocument = async(e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsGeneratingDoc(true);
    try {
      const referentEmail = getReferentEmailForOwner();
      await generateRgpdVisitDocx(getOwnerCompany(owner), jwt, referentEmail ?? getServedReferentEmail(owner));
    } catch (error) {
      console.error('Error generating RGPD document:', error);
      alert('Erreur lors de la génération du document RGPD.');
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const getCivilityLabel = (civility: number): string => {
    switch (civility) {
    case 1: return 'M.';
    case 2: return 'Mme';
    case 3: return 'Société';
    default: return '';
    }
  };

  const getFullName = (): string => {
    const civility = getCivilityLabel(owner.civility);
    const firstname = owner.firstname || '';
    const lastname = owner.lastname || '';
    return `${civility} ${firstname} ${lastname}`.trim();
  };

  const getFullAddress = (): string => {
    const address = owner.address || '';
    const postalCode = owner.postalCode || '';
    const city = owner.city || '';
    return `${address}${address ? ', ' : ''}${postalCode} ${city}`.trim();
  };

  const getPropertyDetails = (): string => {
    const lotNumber = owner.lotNumber ? `Lot ${owner.lotNumber}` : '';
    const floorNumber = owner.floorNumber ? `Étage ${owner.floorNumber}` : '';
    const doorNumber = owner.doorNumber ? `Porte ${owner.doorNumber}` : '';
    const surface = owner.surface ? `${owner.surface} m²` : '';
    
    return [lotNumber, floorNumber, doorNumber, surface]
      .filter(item => item)
      .join(' - ');
  };

  return (
    <Popup 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`Suivi du rendez-vous avec ${getFullName()}`}
      width="800px"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <div className="mb-4">
            <Label htmlFor="owner" value="Propriétaire" />
            <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="font-medium">{getFullName()}</p>
              <p className="text-sm text-gray-500">{getFullAddress()}</p>
              <p className="text-sm text-gray-500">
                {owner.phone ? `Tél : ${owner.phone}` : 'Téléphone non renseigné'} 
                {owner.email ? ` | Email : ${owner.email}` : ' | Email non renseigné'}
              </p>
              <p className="text-sm text-gray-500">Propriété : {getPropertyDetails()}</p>
              {owner.livingIn && (
                <p className="mt-1 text-sm font-medium text-blue-600">Résident sur place</p>
              )}
            </div>
          </div>

          <div className="mb-6 space-y-4">
            <div>
              <Label value="Document RGPD" />
              <div className="mt-2 space-y-4">
                <div className="flex w-full items-center justify-center">
                  <button 
                    className="rounded bg-rgpd-blue px-4 py-1 text-sm text-white hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 focus:outline-none disabled:opacity-50"
                    onClick={handleDownloadRGPDDocument}
                    disabled={isGeneratingDoc}
                  >
                    {isGeneratingDoc ? 'Génération en cours...' : 'Télécharger le document RGPD'}
                  </button>
                </div>
                
                <div>
                  <FileInput
                    ref={signedFileInputRef}
                    onChange={handleFileChange}
                    helperText="Télécharger le document signé"
                  />
                  {formData.signedFile && (
                    <p className="mt-1 text-sm text-green-600">
                      Document signé enregistré le {formData.signedAt}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label value="État du dossier" className="text-lg font-medium" />
                <div className="mt-2 space-y-2 rounded-lg border p-4">
                  <div className="flex items-center space-x-2">
                    <ToggleSwitch
                      checked={formData.professionalActionDone}
                      onChange={handleToggleChange('professionalActionDone')}
                    />
                    <Label value="Estimation du bien effectuée" />
                  </div>
                  <div className="-mt-1 ml-10 text-sm text-gray-500">
                    L'action professionnelle inclut l'estimation et la constitution du dossier
                  </div>
                  
                  <div className="mt-4 flex items-center space-x-2">
                    <ToggleSwitch
                      checked={formData.clientClosed}
                      onChange={handleToggleChange('clientClosed')}
                      disabled={!formData.professionalActionDone}
                    />
                    <Label value={formData.professionalActionDone ? 'Converti en client' : 'Dossier finalisé (nécessite action professionnelle)'} 
                      className={!formData.professionalActionDone ? 'text-gray-400' : ''} />
                  </div>
                  {formData.clientClosed && (
                    <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-3">
                      <p className="text-sm font-medium text-green-700">
                        Le propriétaire sera converti en client {formData.meetingWithType === 'sales-manager' ? 'pour un mandat de vente' : 'de gestion locative'}
                      </p>
                    </div>
                  )}
                  
                  {!formData.clientClosed && formData.professionalActionDone && (
                    <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                      <p className="text-sm font-medium text-yellow-700">
                        Le propriétaire sera classé comme refusé/sans suite
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="comment" value="Commentaire" />
              <Textarea
                id="comment"
                name="comment"
                value={formData.comment || ''}
                onChange={handleInputChange}
                rows={4}
                className="mt-1"
                placeholder="Ajouter des notes ou commentaires sur cette interaction..."
              />
            </div>
          </div>
        </div>
      </form>
      <div className="mt-6 flex w-full justify-between">
        <Button color="gray" onClick={onClose}>
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="blue"
          disabled={!formData.signedFile || isSubmitting}
        >
          {isSubmitting ? 'Envoi en cours...' : 
            (formData.signedFile ? 'Enregistrer le suivi' : 'Document RGPD requis')}
        </Button>
      </div>
    </Popup>
  );
};

export default LeadForm; 